import { formatarCep, formatarData, formatarTelefone } from '@/lib/formato';
import { Checkbox } from '@/ui/primitives/checkbox';
import { type Cnae, type InscricaoEstadual, type RegimeVigencia, UFS } from '@fiscoai/contracts';

import { type ChaveDiferenca, type DiferencasCartao } from './diferencas-cartao';
import { CRT, REGIME } from './rotulos';

const listaCnaes = (cnaes: readonly Cnae[]) =>
  cnaes.map((c) => `${c.codigo}${c.principal ? ' (principal)' : ''}`).join(', ') || '—';

const listaInscricoes = (ies: readonly InscricaoEstadual[]) =>
  ies.map((i) => `${i.uf} ${i.inscricao}`).join(', ') || '—';

const listaRegimes = (regimes: readonly RegimeVigencia[]) =>
  regimes
    .map(
      (r) =>
        `${REGIME[r.regime]} ${formatarData(r.inicio)} → ${r.fim ? formatarData(r.fim) : 'atual'}`,
    )
    .join('; ') || '—';

/** Valor como a pessoa lê: data dd/mm/aaaa, telefone e CEP com máscara, estado por extenso. */
function exibir(chave: ChaveDiferenca, valor: string | null): string {
  if (valor === null) return '—';
  if (chave === 'inicioAtividades') return formatarData(valor);
  if (chave === 'telefone') return formatarTelefone(valor);
  if (chave === 'endereco.cep') return formatarCep(valor);
  if (chave === 'endereco.uf') return UFS.find((u) => u.sigla === valor)?.nome ?? valor;
  if (chave === 'crt') return CRT[valor] ?? valor;
  return valor;
}

interface Props {
  diferencas: DiferencasCartao;
  marcadas: ReadonlySet<ChaveDiferenca>;
  alternar: (chave: ChaveDiferenca) => void;
  cnaes: boolean;
  aoMudarCnaes: (marcado: boolean) => void;
  regimes: boolean;
  aoMudarRegimes: (marcado: boolean) => void;
  inscricoes: boolean;
  aoMudarInscricoes: (marcado: boolean) => void;
}

/** Rótulo da coluna, visível só no celular (onde a linha vira um bloco empilhado). */
const RotuloCelular = ({ children }: { children: string }) => (
  <span className="block text-xs font-normal text-texto-suave no-underline md:hidden">
    {children}
  </span>
);

function LinhasCampos(p: Props) {
  return (
    <table aria-label="Diferenças no cadastro" className="w-full text-sm">
      <thead className="hidden text-left text-xs text-texto-suave md:table-header-group">
        <tr>
          <th className="w-[38%] pb-2 font-medium">Campo</th>
          <th className="w-[24%] pb-2 pr-4 font-medium">Atual</th>
          <th className="pb-2 font-medium">Na Receita</th>
        </tr>
      </thead>
      {/* No celular cada linha vira um bloco: o campo em cima, atual e Receita lado a lado. */}
      <tbody className="divide-y divide-borda">
        {p.diferencas.campos.map((d) => (
          <tr
            key={d.chave}
            className="grid animate-surgir grid-cols-2 gap-x-3 gap-y-1 py-2 align-top md:table-row md:py-0"
          >
            <td className="col-span-2 md:py-2 md:pr-3">
              <Checkbox
                rotulo={d.rotulo}
                marcado={p.marcadas.has(d.chave)}
                aoMudar={() => {
                  p.alternar(d.chave);
                }}
              />
            </td>
            <td className="break-words text-texto-suave md:py-2 md:pr-4">
              <RotuloCelular>Atual</RotuloCelular>
              <span className="line-through decoration-texto-fraco/50">
                {exibir(d.chave, d.atual)}
              </span>
            </td>
            <td className="break-words font-medium text-marca md:py-2">
              <RotuloCelular>Na Receita</RotuloCelular>
              {exibir(d.chave, d.receita)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Campo | Atual | Na Receita — só o que difere; cada linha escolhida por checkbox. */
export function TabelaDiferencas(p: Props) {
  const { diferencas } = p;
  return (
    <div className="grid gap-4">
      {diferencas.campos.length > 0 && <LinhasCampos {...p} />}
      {diferencas.cnaes && (
        <div className="grid gap-1 break-words rounded-cartao border border-borda p-3 text-sm">
          <Checkbox
            rotulo={`CNAEs (${String(diferencas.cnaes.receita.length)} na Receita)`}
            marcado={p.cnaes}
            aoMudar={p.aoMudarCnaes}
          />
          <p className="text-texto-suave">Atual: {listaCnaes(diferencas.cnaes.atual)}</p>
          <p>Na Receita: {listaCnaes(diferencas.cnaes.receita)}</p>
        </div>
      )}
      {diferencas.inscricoes && (
        <div className="grid gap-1 break-words rounded-cartao border border-borda p-3 text-sm">
          <Checkbox
            rotulo={`Inscrições estaduais (${String(diferencas.inscricoes.novas.length)} a acrescentar)`}
            descricao="Só UFs ainda sem inscrição no cadastro; as atuais ficam."
            marcado={p.inscricoes}
            aoMudar={p.aoMudarInscricoes}
          />
          <p className="text-texto-suave">Atual: {listaInscricoes(diferencas.inscricoes.atual)}</p>
          <p>Na Receita: {listaInscricoes(diferencas.inscricoes.novas)}</p>
        </div>
      )}
      {diferencas.regimes && (
        <div className="grid gap-1 rounded-cartao border border-borda p-3 text-sm">
          <Checkbox
            rotulo="Regime tributário (sugerido pela Receita)"
            descricao="Confira com o fiscal antes de aplicar."
            marcado={p.regimes}
            aoMudar={p.aoMudarRegimes}
          />
          {diferencas.regimes.incompleto && (
            <p className="rounded-controle bg-alerta-suave px-2 py-1 text-alerta">
              Esta consulta não informa a forma de tributação declarada (ECF): a sugestão traz só o
              Simples/MEI. Informe o Lucro Real ou Presumido na aba Tributário.
            </p>
          )}
          <p className="text-texto-suave">Atual: {listaRegimes(diferencas.regimes.atual)}</p>
          <p>Sugerido: {listaRegimes(diferencas.regimes.receita)}</p>
        </div>
      )}
    </div>
  );
}
